import { Loader2, Check, X } from "lucide-react";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

/** Shown the instant the user hits submit: the arithmetic we can prove locally, while the AI review streams in. */
export const PendingPanel = ({ lines = [], label = "Reviewing entry", partial = null }) => {
  const debit = lines.filter((l) => l.type === "debit").reduce((s, l) => s + Number(l.amount || 0), 0);
  const credit = lines.filter((l) => l.type === "credit").reduce((s, l) => s + Number(l.amount || 0), 0);
  const balanced = lines.length > 0 && Math.abs(debit - credit) < 0.01;

  return (
    <div className="animate-slide-left border border-stone-200 bg-white" data-testid="pending-panel">
      <div className="flex items-center gap-3 border-b border-stone-200 bg-[#0f172a] p-6 text-white">
        <Loader2 className="h-5 w-5 animate-spin" />
        <div>
          <p className="font-display text-xl font-bold tracking-tight">{label}…</p>
          <p className="mt-1 font-mono-data text-xs uppercase tracking-wider opacity-70">arithmetic first, standards in a moment</p>
        </div>
      </div>

      {lines.length > 0 && (
        <>
          <div className="grid grid-cols-2 border-b border-stone-200">
            <div className="border-r border-stone-200 p-6">
              <p className="overline text-slate-500">Total Debit</p>
              <p className="mt-1 font-mono-data text-lg font-semibold" data-testid="pending-debit">{money(debit)}</p>
            </div>
            <div className="p-6">
              <p className="overline text-slate-500">Total Credit</p>
              <p className="mt-1 font-mono-data text-lg font-semibold" data-testid="pending-credit">{money(credit)}</p>
            </div>
          </div>
          <div className="flex items-start gap-3 border-b border-stone-200 p-6">
            <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center ${balanced ? "bg-emerald-700" : "bg-red-700"} text-white`}>
              {balanced ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
            </span>
            <div>
              <p className="text-sm font-medium">Double entry balance</p>
              <p className="font-mono-data text-xs text-slate-500" data-testid="pending-balance">
                {balanced ? "Debits equal credits." : `Difference of ${money(Math.abs(debit - credit))}.`}
              </p>
            </div>
          </div>
        </>
      )}

      <div className="space-y-3 p-6">
        {partial?.explanation || partial?.narration ? (
          <div data-testid="stream-partial">
            {partial.verdict && (
              <p className="overline mb-3 text-slate-500" data-testid="stream-verdict">
                {partial.verdict.replace("_", " ")}
                {partial.confidence ? ` · ${partial.confidence} confidence` : ""}
              </p>
            )}
            <p className="text-sm leading-relaxed text-slate-700">
              {partial.narration ? <span className="mb-2 block font-mono-data text-xs text-slate-500" data-testid="stream-narration">{partial.narration}</span> : null}
              {partial.explanation}
              <span className="ml-0.5 inline-block h-4 w-[2px] animate-pulse bg-[#0f172a] align-middle" />
            </p>
          </div>
        ) : (
          <>
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-3 animate-pulse bg-stone-100" style={{ width: `${100 - i * 18}%`, animationDelay: `${i * 120}ms` }} />
            ))}
            <p className="pt-2 font-mono-data text-[11px] text-slate-400">checking classification, the applicable standard, GST and Companies Act impact…</p>
          </>
        )}
      </div>
    </div>
  );
};
